import { LYTPoolAbi } from '@shared/Abis'
import { logOT } from '@shared/loggingShared'
import { Contract } from 'ethers'
import { useCallback, useEffect, useState } from 'react'
import { useOtChainState } from '@react/redux/store'

export const useExchangeRate = (props: { initialExchangeRate: bigint; refetchInterval?: number }) => {
  const { chainConfiguration, stageConfiguration } = useOtChainState()
  const { websocketProvider } = chainConfiguration
  const poolAddr = stageConfiguration.lytPoolAddr

  const [exchangeRate, setExchangeRate] = useState<bigint>(props.initialExchangeRate || 0n)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchExchangeRate = useCallback(async () => {
    if (!websocketProvider || !poolAddr) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const poolContract = new Contract(poolAddr, LYTPoolAbi, websocketProvider)
      const exchangeRate = await poolContract.exchangeRate()
      setExchangeRate(exchangeRate)
      setError(null) // Clear error on success
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch exchange rate')
      setError(error)
      logOT.error('Error fetching exchange rate:', error)
    } finally {
      setIsLoading(false) // Always ensure loading is set to false
    }
  }, [poolAddr, websocketProvider])

  const refetch = useCallback(async () => {
    await fetchExchangeRate()
  }, [fetchExchangeRate])

  useEffect(() => {
    if (!websocketProvider || !poolAddr) {
      setIsLoading(false)
      return
    }

    let contract: Contract | null = null
    let isMounted = true
    let intervalId: NodeJS.Timeout | null = null

    const setupEventListeners = async () => {
      try {
        // Initial balance fetch
        await fetchExchangeRate()

        if (!isMounted) return

        // Set up event listeners
        contract = new Contract(poolAddr, LYTPoolAbi, websocketProvider)

        // Create specific filters for Transfer events involving accountAddress
        const exchangeRateSetFilter = contract.filters.ExchangeRateSet()

        // If exchangeRate set, wait 3 second and refetch exchange rate
        // the wait is to make sure that rpc node will return the new value
        const handleExchangeRateSet = (_log: any) => {
          try {
            // Refetch exchange rate after blockchain state update
            setTimeout(() => {
              if (isMounted) {
                fetchExchangeRate()
              }
            }, 1000)
          } catch (parseError) {
            logOT.error('Error handling set exchange rate event:', parseError)
          }
        }

        contract.on(exchangeRateSetFilter, handleExchangeRateSet)

        // Optional polling fallback
        if (props.refetchInterval && props.refetchInterval > 0) {
          intervalId = setInterval(() => {
            if (isMounted) {
              fetchExchangeRate()
            }
          }, props.refetchInterval)
        }
      } catch (err) {
        if (isMounted) {
          const error = err instanceof Error ? err : new Error('Failed to setup event listeners')
          setError(error)
          logOT.error('Error setting up ERC20 event listeners:', error)
        }
      }
    }

    setupEventListeners()

    // Cleanup function
    return () => {
      isMounted = false

      if (contract) {
        try {
          contract.removeAllListeners()
        } catch (err) {
          logOT.error('Error cleaning up ERC20 event listeners:', err)
        }
      }

      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [fetchExchangeRate, poolAddr, websocketProvider])

  return { exchangeRate, isLoading, refetch, error }
}
